/* QuickLabEntryCard — бърз entry на основни лабораторни показатели (iter421.8).
 *
 * 9 полета (всички опц.): кръвна захар, инсулин, HbA1c, хол., LDL, HDL, TG, vitD, TSH.
 * При попълване на glucose+insulin → автоматично се изчислява HOMA-IR.
 * При запис: създава lab_panel snapshot в HI → +25 точки depth на HI Score.
 *
 * Tone: интерпретациите са "оптимален" / "област за подобрение" / "заслужава проследяване".
 */
import React, { useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FlaskConical, Save, Loader2, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const FIELDS = [
  { id: "fasting_glucose_mmol_l", bg: "Кръвна захар (на гладно)",     en: "Fasting glucose",   unit: "mmol/L", step: 0.1, ph: "5.0" },
  { id: "insulin_mu_l",            bg: "Инсулин (на гладно)",          en: "Fasting insulin",   unit: "mU/L",   step: 0.1, ph: "6.0" },
  { id: "hba1c_pct",               bg: "Гликиран хемоглобин (HbA1c)", en: "HbA1c",             unit: "%",      step: 0.1, ph: "5.4" },
  { id: "total_cholesterol_mmol_l", bg: "Общ холестерол",              en: "Total cholesterol", unit: "mmol/L", step: 0.1, ph: "4.8" },
  { id: "ldl_mmol_l",              bg: "LDL холестерол",               en: "LDL",               unit: "mmol/L", step: 0.1, ph: "2.5" },
  { id: "hdl_mmol_l",              bg: "HDL холестерол",               en: "HDL",               unit: "mmol/L", step: 0.1, ph: "1.4" },
  { id: "triglycerides_mmol_l",    bg: "Триглицериди",                 en: "Triglycerides",     unit: "mmol/L", step: 0.1, ph: "1.2" },
  { id: "vitamin_d_ng_ml",         bg: "Витамин D (25-OH)",           en: "Vitamin D",         unit: "ng/mL",  step: 1,   ph: "35" },
  { id: "tsh_mu_l",                bg: "TSH",                          en: "TSH",               unit: "mU/L",   step: 0.01, ph: "1.8" },
];

export default function QuickLabEntryCard({ onSaved }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const filledCount = Object.values(vals).filter((v) => v !== "" && v != null).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(vals).forEach(([k, v]) => {
        if (v !== "" && !isNaN(Number(v))) payload[k] = Number(v);
      });
      if (Object.keys(payload).length === 0) {
        toast.error(bg ? "Попълни поне един показател" : "Fill at least one field");
        setSaving(false);
        return;
      }
      const r = await axios.post(`${API}/health-intelligence/quick-lab-entry`, payload);
      toast.success(bg ? `Записани ${r.data?.biomarker_count} показателя ✓` : `Saved ${r.data?.biomarker_count} markers ✓`);
      setVals({});
      setExpanded(false);
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // iter425 — Pydantic validation връща list of {loc, msg, type} обекти.
      // Преведи ги в разбираемо BG съобщение, вместо просто "Грешка".
      if (Array.isArray(detail)) {
        const firstErr = detail[0] || {};
        const field = Array.isArray(firstErr.loc) ? firstErr.loc[firstErr.loc.length - 1] : "";
        const fieldDef = FIELDS.find((f) => f.id === field);
        const fieldLabel = fieldDef ? (bg ? fieldDef.bg : fieldDef.en) : (field || "поле");
        // Detect type: range-error / decimal / etc.
        const msg = (firstErr.msg || "").toLowerCase();
        if (msg.includes("greater than") || msg.includes("less than") || msg.includes("equal to")) {
          toast.error(bg
            ? `${fieldLabel}: стойността е извън допустимия диапазон. Проверете единицата.`
            : `${fieldLabel}: value out of range. Check the unit.`);
        } else {
          toast.error(bg ? `${fieldLabel}: невалидна стойност` : `${fieldLabel}: invalid value`);
        }
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (e.response?.status === 401) {
        toast.error(bg ? "Сесията е изтекла. Моля, влезте отново." : "Session expired. Please log in.");
      } else {
        toast.error(bg ? "Грешка при запазване на лабораторни данни" : "Failed to save lab data");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}
          data-testid="quick-lab-entry-card">
      <CardContent className="p-4">
        <button onClick={() => setExpanded((x) => !x)}
                className="w-full flex items-center gap-2 text-left"
                data-testid="quick-lab-toggle">
          <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-sky-500 to-cyan-500 text-white">
            <FlaskConical className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Бърз въвод на лабораторни" : "Quick lab entry"}
            </h3>
            <p className={`text-[10.5px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? "Кръвна захар, инсулин, HbA1c, холестерол, вит. D, TSH — увеличава дълбочината на HI Score (+25 точки)"
                : "Glucose, insulin, HbA1c, cholesterol, vit D, TSH — boosts HI Score depth (+25 pts)"}
            </p>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"}`}>
            {expanded ? (bg ? "Скрий" : "Hide") : (bg ? "Отвори" : "Open")}
          </span>
        </button>

        {expanded && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {FIELDS.map((f) => (
                <div key={f.id}>
                  <label className={`text-[10.5px] uppercase tracking-wider font-bold block mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? f.bg : f.en}
                  </label>
                  <div className="relative">
                    <Input type="number" step={f.step} placeholder={f.ph}
                           value={vals[f.id] || ""}
                           onChange={(e) => setVals((v) => ({ ...v, [f.id]: e.target.value }))}
                           className={`pr-16 ${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                           data-testid={`lab-${f.id}`} />
                    <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold pointer-events-none ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                      {f.unit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className={`text-[10px] flex items-center gap-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                <Info className="w-3 h-3" />
                {bg
                  ? `${filledCount} от 9 попълнени · Глюкоза + Инсулин → HOMA-IR автоматично`
                  : `${filledCount} of 9 filled · Glucose + Insulin → HOMA-IR auto`}
              </p>
              <Button onClick={handleSave} disabled={saving || filledCount === 0}
                      className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white"
                      data-testid="quick-lab-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                {bg ? "Запази показатели" : "Save markers"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
